// subscriptionModule.js
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';

const subscriptionModule = {
  namespaced: true,

  state: {
    subscription: null,        // Current user subscription
    plans: [],                 // Available subscription plans
    usage: [],                 // Usage history
    topupOptions: [],          // Top-up options
    loading: false,
    error: null
  },

  mutations: {
    setSubscription(state, subscription) {
      state.subscription = subscription;
    },
    setPlans(state, plans) {
      state.plans = plans;
    },
    setUsage(state, usage) {
      state.usage = usage;
    },
    setTopupOptions(state, options) {
      state.topupOptions = options;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    clearError(state) {
      state.error = null;
    }
  },

  actions: {
    /**
     * Fetch current subscription status
     */
    fetchSubscriptionStatus({ commit, rootState }, chatbotName = 'meetscribe') {
      // Check if user is authenticated before making API call
      // Note: user module is namespaced, so structure is rootState.user.user
      const userModule = rootState.user;
      const isAuthenticated = userModule && userModule.isAuthenticated && userModule.user && userModule.user.tg_user_id;

      if (!isAuthenticated) {
        // User not logged in - set subscription to null without error
        commit('setSubscription', null);
        return Promise.resolve(null);
      }

      commit('setLoading', true);
      commit('clearError');

      return axiosConfig
        .get(API_URL, {
          params: {
            action: 'get_subscription_status',
            chatbot_name: chatbotName
          }
        })
        .then(response => {
          if (response.data.success) {
            commit('setSubscription', response.data.data || response.data.subscription);
            return response.data.data || response.data.subscription;
          } else {
            // Auth required is not a real error - user just not logged in
            if (response.data.error === 'Auth required') {
              commit('setSubscription', null);
              return null;
            }
            throw new Error(response.data.error || 'Failed to fetch subscription status');
          }
        })
        .catch(error => {
          console.error('Error fetching subscription status:', error);
          // Don't set error for auth issues
          if (error.message !== 'Auth required') {
            commit('setError', error.message || 'Failed to fetch subscription status');
          }
          commit('setSubscription', null);
          return null;
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    /**
     * Fetch available subscription plans
     */
    fetchPlans({ commit }, chatbotName = 'meetscribe') {
      commit('setLoading', true);
      commit('clearError');

      return axiosConfig
        .get(API_URL, {
          params: {
            action: 'get_subscription_plans',
            chatbot_name: chatbotName
          }
        })
        .then(response => {
          if (response.data.success) {
            const plans = response.data.data || response.data.plans || {};
            commit('setPlans', plans);
            return plans;
          } else {
            throw new Error(response.data.error || 'Failed to fetch plans');
          }
        })
        .catch(error => {
          console.error('Error fetching subscription plans:', error);
          commit('setError', error.message || 'Failed to fetch plans');
          throw error;
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    /**
     * Cancel subscription auto-renewal
     */
    cancelSubscription({ commit, dispatch }, { subscriptionId, chatbotName = 'meetscribe' }) {
      commit('setLoading', true);
      commit('clearError');

      return axiosConfig
        .post(API_URL + '?action=cancel_subscription', {
          subscription_id: subscriptionId,
          chatbot_name: chatbotName
        })
        .then(response => {
          if (response.data.success) {
            // Refresh subscription status after cancellation
            dispatch('fetchSubscriptionStatus', chatbotName);
            return response.data;
          } else {
            throw new Error(response.data.error || 'Failed to cancel subscription');
          }
        })
        .catch(error => {
          console.error('Error canceling subscription:', error);
          commit('setError', error.message || 'Failed to cancel subscription');
          throw error;
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    /**
     * Fetch usage history
     */
    fetchUsage({ commit, rootState }, { chatbotName = 'meetscribe', limit = 50 }) {
      // Check if user is authenticated
      const userModule = rootState.user;
      const isAuthenticated = userModule && userModule.isAuthenticated && userModule.user && userModule.user.tg_user_id;
      if (!isAuthenticated) {
        commit('setUsage', []);
        return Promise.resolve([]);
      }

      commit('setLoading', true);
      commit('clearError');

      return axiosConfig
        .get(API_URL, {
          params: {
            action: 'get_subscription_usage',
            chatbot_name: chatbotName,
            limit: limit
          }
        })
        .then(response => {
          if (response.data.success) {
            const usage = response.data.data?.history || response.data.usage || [];
            commit('setUsage', usage);
            return usage;
          } else {
            if (response.data.error === 'Auth required') {
              commit('setUsage', []);
              return [];
            }
            throw new Error(response.data.error || 'Failed to fetch usage');
          }
        })
        .catch(error => {
          console.error('Error fetching usage:', error);
          if (error.message !== 'Auth required') {
            commit('setError', error.message || 'Failed to fetch usage');
          }
          commit('setUsage', []);
          return [];
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    /**
     * Fetch top-up options (balance replenishment amounts)
     */
    fetchTopupOptions({ commit, state }, chatbotName = 'meetscribe') {
      commit('setLoading', true);
      commit('clearError');

      return axiosConfig
        .get(API_URL, {
          params: {
            action: 'get_topup_options',
            chatbot_name: chatbotName
          }
        })
        .then(response => {
          if (response.data.success) {
            const options = response.data.data?.options || [];
            commit('setTopupOptions', options);
            // Also update balance if returned (preserve existing subscription data)
            if (response.data.data?.current_balance !== undefined) {
              commit('setSubscription', {
                ...state.subscription,
                balance: response.data.data.current_balance
              });
            }
            return options;
          } else {
            throw new Error(response.data.error || 'Failed to fetch top-up options');
          }
        })
        .catch(error => {
          console.error('Error fetching top-up options:', error);
          commit('setError', error.message || 'Failed to fetch top-up options');
          return [];
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    /**
     * Initialize subscription data (status + plans)
     */
    initSubscriptionData({ dispatch }, chatbotName = 'meetscribe') {
      return Promise.all([
        dispatch('fetchSubscriptionStatus', chatbotName),
        dispatch('fetchPlans', chatbotName)
      ]);
    },

    /**
     * Clear subscription data (on logout)
     */
    clearSubscriptionData({ commit }) {
      commit('setSubscription', null);
      commit('setUsage', []);
      commit('clearError');
    }
  },

  getters: {
    /**
     * Check if user has active subscription
     */
    hasActiveSubscription(state) {
      if (!state.subscription) return false;
      // Support both API formats:
      // - Old: is_active, plan_id
      // - New: has_subscription, status
      return state.subscription.is_active ||
             state.subscription.has_subscription ||
             state.subscription.status === 'active';
    },

    /**
     * Get remaining minutes in current period
     */
    remainingMinutes(state) {
      if (!state.subscription) return 0;
      return state.subscription.minutes_remaining || 0;
    },

    /**
     * Get total minutes in plan
     */
    totalMinutes(state) {
      if (!state.subscription) return 0;
      // Support both: minutes_total (old) and minutes_limit (new API)
      return state.subscription.minutes_total || state.subscription.minutes_limit || 0;
    },

    /**
     * Get used minutes in current period
     */
    usedMinutes(state) {
      if (!state.subscription) return 0;
      return state.subscription.minutes_used || 0;
    },

    /**
     * Get days left until subscription expires
     */
    daysLeft(state) {
      if (!state.subscription || !state.subscription.expires_at) return 0;

      const expiresAt = new Date(state.subscription.expires_at);
      const now = new Date();
      const diffTime = expiresAt - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return Math.max(0, diffDays);
    },

    /**
     * Get current plan name
     */
    currentPlanName(state) {
      if (!state.subscription) return 'Free';
      return state.subscription.plan_name || 'Free';
    },

    /**
     * Get current plan ID
     */
    currentPlanId(state) {
      if (!state.subscription) return 'free';
      return state.subscription.plan_id || 'free';
    },

    /**
     * Check if subscription has auto-renewal enabled
     */
    hasAutoRenewal(state) {
      if (!state.subscription) return false;
      // Support both: auto_renewal (old) and auto_renew (new API)
      return state.subscription.auto_renewal || state.subscription.auto_renew;
    },

    /**
     * Get subscription expiration date formatted
     */
    expiresAt(state) {
      if (!state.subscription || !state.subscription.expires_at) return null;
      return state.subscription.expires_at;
    },

    /**
     * Get usage percentage (0-100)
     */
    usagePercentage(state) {
      if (!state.subscription) return 0;
      const total = state.subscription.minutes_total || state.subscription.minutes_limit || 0;
      if (!total) return 0;
      const used = state.subscription.minutes_used || 0;
      return Math.min(100, Math.round((used / total) * 100));
    },

    /**
     * Get all plans
     */
    getPlans(state) {
      return state.plans;
    },

    /**
     * Get subscription object
     */
    getSubscription(state) {
      return state.subscription;
    },

    /**
     * Get usage history
     */
    getUsage(state) {
      return state.usage;
    },

    /**
     * Check if loading
     */
    isLoading(state) {
      return state.loading;
    },

    /**
     * Get error message
     */
    getError(state) {
      return state.error;
    },

    /**
     * Check if user is on free plan (pay-as-you-go)
     */
    isFreePlan(state) {
      if (!state.subscription) return true;
      // Check has_subscription first (new API format)
      if (state.subscription.has_subscription === true) return false;
      if (state.subscription.status === 'active') return false;
      // Then check plan_id for backwards compatibility (old API format)
      if (state.subscription.plan_id && state.subscription.plan_id !== 'free') return false;
      return true;
    },

    /**
     * Get user balance (for pay-as-you-go)
     */
    getBalance(state) {
      if (!state.subscription) return 0;
      return state.subscription.balance || 0;
    },

    /**
     * Get top-up URL for balance replenishment
     */
    getTopupUrl(state) {
      if (!state.subscription) return null;
      return state.subscription.topup_url || null;
    },

    /**
     * Check if user has non-zero balance
     */
    hasBalance(state) {
      if (!state.subscription) return false;
      return (state.subscription.balance || 0) > 0;
    },

    /**
     * Get top-up options
     */
    getTopupOptions(state) {
      return state.topupOptions || [];
    }
  }
};

export default subscriptionModule;
